import type { WorkerRole } from "@cmmd-forge/contracts";

import { cn } from "~/lib/utils";

interface WorkerRoleBadgeProps {
  readonly role: WorkerRole | null | undefined;
  readonly className?: string;
}

/**
 * Small inline label identifying a worker's role. Colors are stable so they
 * double as visual anchors across the WorkBar agents row, sidebar, and
 * chat header. Unknown / null roles render as a neutral `Worker` badge.
 */
export function WorkerRoleBadge({ role, className }: WorkerRoleBadgeProps) {
  const { label, tone } = formatWorkerRole(role);
  return (
    <span
      data-testid="worker-role-badge"
      className={cn(
        "inline-flex items-center rounded-sm px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
        tone,
        className,
      )}
    >
      {label}
    </span>
  );
}

interface FormattedRole {
  readonly label: string;
  readonly tone: string;
}

/**
 * Canonical label + Tailwind tone classes per role. Kept pure so snapshots &
 * theme tests can assert on the exact mapping without mounting the component.
 *
 * Roles draw only from the categorical hues, never from the severity families:
 * a role is an identity, not a verdict. "QA" tinted `success` green reads as a
 * passing state rather than as a person, and it would also change meaning the
 * day the success hue is retuned for contrast. See DESIGN.md -> "Categorical
 * hues".
 */
export function formatWorkerRole(role: WorkerRole | null | undefined): FormattedRole {
  switch (role) {
    case "swe":
      return {
        label: "SWE",
        tone: "bg-hue-sky-surface text-hue-sky-foreground",
      };
    case "qa":
      return {
        label: "QA",
        tone: "bg-hue-cyan-surface text-hue-cyan-foreground",
      };
    case "reviewer":
      return {
        label: "Review",
        tone: "bg-hue-violet-surface text-hue-violet-foreground",
      };
    case "designer":
      return {
        label: "Design",
        tone: "bg-hue-pink-surface text-hue-pink-foreground",
      };
    case "pm":
      return {
        label: "PM",
        tone: "bg-hue-orange-surface text-hue-orange-foreground",
      };
    case "architect":
      return {
        label: "Arch",
        tone: "bg-hue-indigo-surface text-hue-indigo-foreground",
      };
    default:
      return {
        label: "Worker",
        tone: "bg-muted text-muted-foreground",
      };
  }
}
